import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_BATCH_SIZE = 50;

export function useLoadMoreOnScroll<T>(items: T[], batchSize = DEFAULT_BATCH_SIZE) {
  const [visibleCount, setVisibleCount] = useState(batchSize);
  const sentinelRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    setVisibleCount(batchSize);
  }, [items, batchSize]);

  const loadMore = useCallback(() => {
    setVisibleCount((current) => Math.min(current + batchSize, items.length));
  }, [batchSize, items.length]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || visibleCount >= items.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMore();
        }
      },
      { rootMargin: "240px 0px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [items.length, loadMore, visibleCount]);

  const visibleItems = items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;

  return { visibleItems, hasMore, sentinelRef, visibleCount };
}
